import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Plus, Search, Building2, Edit, Trash2, CheckCircle, XCircle, 
  Shield, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InsuranceProviders = () => {
  const { isOwner } = useAuth();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    short_code: '',
    notes: '',
    active: true
  });

  const fetchProviders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/insurance-providers`);
      setProviders(response.data);
    } catch (error) {
      console.error('Failed to fetch insurance providers:', error);
      toast.error('Failed to load insurance providers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const resetForm = () => {
    setFormData({
      name: '',
      short_code: '',
      notes: '',
      active: true
    });
    setEditingProvider(null);
  };

  const handleOpenAdd = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleOpenEdit = (provider) => {
    setFormData({
      name: provider.name,
      short_code: provider.short_code,
      notes: provider.notes || '',
      active: provider.active
    });
    setEditingProvider(provider);
    setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Provider name is required');
      return;
    }
    if (!formData.short_code.trim()) {
      toast.error('Short code is required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingProvider) {
        await axios.put(`${API}/insurance-providers/${editingProvider.id}`, formData);
        toast.success('Insurance provider updated');
      } else {
        await axios.post(`${API}/insurance-providers`, formData);
        toast.success('Insurance provider created');
      }
      setShowAddModal(false);
      resetForm();
      fetchProviders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save provider');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (provider) => {
    if (!window.confirm(`Are you sure you want to deactivate ${provider.name}?`)) return;
    
    try {
      await axios.delete(`${API}/insurance-providers/${provider.id}`);
      toast.success('Insurance provider deactivated');
      fetchProviders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate provider');
    }
  };

  const filteredProviders = providers.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.short_code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeProviders = filteredProviders.filter(p => p.active);
  const inactiveProviders = filteredProviders.filter(p => !p.active);

  if (!isOwner) {
    return (
      <Layout>
        <div className="page-container flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <Shield className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold text-slate-700">Access Restricted</h2>
            <p className="text-slate-500 mt-2">Only Owner Admin can manage insurance providers</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-container">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Insurance Providers</h1>
            <p className="text-slate-500">Manage insurance providers for billing</p>
          </div>
          <Button onClick={handleOpenAdd} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Provider
          </Button>
        </div>

        {/* Search */}
        <div className="card p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search providers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{providers.length}</p>
                <p className="text-sm text-slate-500">Total Providers</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{activeProviders.length}</p>
                <p className="text-sm text-slate-500">Active</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{inactiveProviders.length}</p>
                <p className="text-sm text-slate-500">Inactive</p>
              </div>
            </div>
          </div>
        </div>

        {/* Providers List */}
        {loading ? (
          <div className="card p-8 text-center">
            <div className="text-slate-500">Loading providers...</div>
          </div>
        ) : filteredProviders.length === 0 ? (
          <div className="card p-8 text-center">
            <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600 font-medium">No insurance providers found</p>
            <p className="text-sm text-slate-500">
              {searchTerm ? 'Try a different search term' : 'Add your first insurance provider'}
            </p>
          </div>
        ) : (
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-200 bg-slate-50">
                  <th className="text-left p-4 font-semibold text-slate-700">Provider</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Code</th>
                  <th className="text-left p-4 font-semibold text-slate-700 hidden md:table-cell">Notes</th>
                  <th className="text-center p-4 font-semibold text-slate-700">Status</th>
                  <th className="text-right p-4 font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProviders.map((provider) => (
                  <tr key={provider.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-blue-600" />
                        </div>
                        <span className="font-medium text-slate-900">{provider.name}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="px-2 py-1 bg-slate-100 rounded text-sm font-mono text-slate-700">
                        {provider.short_code}
                      </span>
                    </td>
                    <td className="p-4 hidden md:table-cell">
                      <span className="text-slate-500 text-sm">{provider.notes || '-'}</span>
                    </td>
                    <td className="p-4 text-center">
                      {provider.active ? (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenEdit(provider)}
                          className="text-blue-600 hover:bg-blue-50"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {provider.active && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeactivate(provider)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Add/Edit Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="sm:max-w-md border-2 border-slate-200">
            <DialogHeader>
              <DialogTitle className="text-slate-900 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                {editingProvider ? 'Edit Insurance Provider' : 'Add Insurance Provider'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pt-4">
              <div>
                <Label htmlFor="name">Provider Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Sagicor Life Jamaica"
                  required
                />
              </div>
              <div>
                <Label htmlFor="short_code">Short Code *</Label>
                <Input
                  id="short_code"
                  value={formData.short_code}
                  onChange={(e) => setFormData({ ...formData, short_code: e.target.value.toUpperCase() })}
                  placeholder="e.g., SAG"
                  maxLength={10}
                  required
                />
                <p className="text-xs text-slate-500 mt-1">Used on invoices and claims</p>
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Input
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Optional notes about this provider"
                />
              </div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <Label>Active Status</Label>
                  <p className="text-xs text-slate-500">Inactive providers cannot be used for new invoices</p>
                </div>
                <Switch
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 border-2"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : editingProvider ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default InsuranceProviders;
